/**
 * DocumentsPage — The Library.
 *
 * Residence Journal · frozen design system.
 * Presentation only — every handler, testid, seed constant and modal
 * preserved from the pre-renovation implementation.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import QuickAddModal from "./components/os/QuickAddModal";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content (unchanged) ─────────────────────────────────────────── */

const SOURCE_LABEL = {
  paper: "Paper", email: "Email", pdf: "PDF", school: "School",
  council: "Council", medical: "Medical", invoice: "Invoice", manual: "Manual",
};

const SEED_CATEGORIES = [
  { id: "identity",   label: "Identity",             sensitive: true,  examples: "Passport · Birth cert · NHI",       count: 6  },
  { id: "health",     label: "Health",               sensitive: true,  examples: "Care plan · Vaccines · Letters",    count: 12 },
  { id: "school",     label: "School",               sensitive: false, examples: "Reports · Permission slips",        count: 9  },
  { id: "financial",  label: "Financial",            sensitive: true,  examples: "Tax · Bank · Investments",          count: 14 },
  { id: "insurance",  label: "Insurance",            sensitive: false, examples: "Home · Vehicle · Life",             count: 6  },
  { id: "property",   label: "Property",             sensitive: false, examples: "Title · Council · Renovations",     count: 5  },
  { id: "vehicle",    label: "Vehicle",              sensitive: false, examples: "WOF · Rego · Service log",          count: 4  },
  { id: "legal",      label: "Legal",                sensitive: true,  examples: "Wills · POAs · Contracts",           count: 3  },
  { id: "benefits",   label: "Benefits / MSD",       sensitive: true,  examples: "Working for Families · Reviews",     count: 2  },
  { id: "warranties", label: "Warranties & manuals", sensitive: false, examples: "Appliances · Solar · Roof",          count: 8  },
  { id: "business",   label: "Business",             sensitive: true,  examples: "Side income · Invoices · GST",       count: 7  },
  { id: "letters",    label: "Letters & templates",  sensitive: false, examples: "Absence · Support letters",           count: 5  },
];

const SEED_ACTIONS = [];

const SEED_RENEWALS = [];

const SEED_CAPTURES = [];

const SEED_DETAIL_DOCS = [];

const SEED_TEMPLATES = [
  { id: "tpl1", title: "School absence note",       desc: "Short formal note for unplanned absences." },
  { id: "tpl2", title: "Medical support request",   desc: "Ask the GP for a supporting letter." },
  { id: "tpl3", title: "Council approval request",  desc: "Polite written request for minor consent." },
  { id: "tpl4", title: "Benefit / MSD support",     desc: "Outline of household circumstances." },
  { id: "tpl5", title: "Insurance claim note",      desc: "Standard structure for claim documentation." },
  { id: "tpl6", title: "Permission form checklist", desc: "Reusable checklist of fields and signatures." },
];

/* ── ARA judgement ────────────────────────────────────────────────────── */
function focalSentence(actions, renewals, details) {
  if (actions.length === 0 && renewals.length === 0 && details.length === 0) {
    return "Add one document you never want to lose.";
  }
  const openHigh = actions.filter((a) => a.status === "Open" && a.priority === "High").length;
  const soonRenewals = renewals.filter((r) => r.daysLeft <= 30).length;
  if (openHigh === 0 && soonRenewals === 0) return "Nothing urgent. The library is calm.";
  if (openHigh > 0 && soonRenewals > 0)     return `${openHigh} document${openHigh === 1 ? '' : 's'} and ${soonRenewals} renewal${soonRenewals === 1 ? '' : 's'} need attention.`;
  if (openHigh > 0)                          return `${openHigh} document${openHigh === 1 ? ' needs' : 's need'} your attention.`;
  return `${soonRenewals} renewal${soonRenewals === 1 ? '' : 's'} coming up soon.`;
}

/* ── The Library ──────────────────────────────────────────────────────── */

export default function DocumentsPage() {
  const { members } = useHomeOSState();
  const { phase } = useLightPhase();

  const [actions, setActions]             = useState([]);
  const [renewals, setRenewals]           = useState([]);
  const [captures, setCaptures]           = useState([]);
  const [details, setDetails]             = useState([]);
  const [openDetailId, setOpenDetailId]   = useState(null);
  const [approvedCount, setApprovedCount] = useState(0);
  const [searchQuery, setSearchQuery]     = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [addOpen, setAddOpen]             = useState(false);

  const DOCUMENT_CATEGORIES = ["All","Family","Health","Insurance","Financial","Legal","Household","School","Vehicle","Other"];

  const filteredDetails = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return details.filter((d) => {
      if (categoryFilter !== "All" && d.category !== categoryFilter) return false;
      if (!q) return true;
      return [d.title, d.category, d.owner, d.status, d.notes, d.source]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [details, searchQuery, categoryFilter]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const markActionDone   = (id) => { setActions((as) => as.map((a) => a.id === id ? { ...a, status: "Done" } : a)); toast.success("Done."); };
  const snoozeAction     = (id) => { setActions((as) => as.map((a) => a.id === id ? { ...a, status: "Snoozed" } : a)); toast("Snoozed."); };
  const followUpAction   = (id) => { setActions((as) => as.map((a) => a.id === id ? { ...a, status: "Follow-up" } : a)); toast("Follow-up added."); };
  const watchRenewal     = (id) => { setRenewals((rs) => rs.map((r) => r.id === id ? { ...r, status: "Watching" } : r)); toast.success("Watched."); };
  const setRenewalReminder = (id) => { toast("Reminder set."); void id; };

  const approveCapture = (id) => {
    const c = captures.find((x) => x.id === id);
    if (c) {
      setApprovedCount((n) => n + 1);
      setDetails((ds) => [{
        id: `dd_${c.id}`, title: c.title, category: c.category, owner: c.owner,
        status: "Filed", source: SOURCE_LABEL[c.source] || "Manual",
        added: new Date().toISOString().slice(0, 10), expiry: "—",
        notes: "Approved from capture inbox.",
        privacy: c.category === "Health" ? "health" : c.category === "Legal" ? "legal" : "shared",
      }, ...ds]);
    }
    setCaptures((cs) => cs.filter((x) => x.id !== id));
    toast.success("Filed.");
  };
  const editCapture    = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Refine below."); scrollTo("documents-detail-view"); };
  const dismissCapture = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Dismissed."); };
  const junkCapture    = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Marked junk."); };

  const updateDetailStatus = (id) => { setDetails((ds) => ds.map((d) => d.id === id ? { ...d, status: d.status === "Active" ? "Filed" : "Active" } : d)); toast.success("Status updated."); };
  const linkTaskLater      = (id) => { toast("Task placeholder."); void id; };
  const markPrivate        = (id) => { setDetails((ds) => ds.map((d) => d.id === id ? { ...d, privacy: "private" } : d)); toast.success("Marked private."); };

  const handleAddDocument = (v) => {
    setDetails((ds) => [{
      id: `dd_${Date.now()}`, title: v.title.trim(), category: v.category || "Other",
      owner: v.owner || "Family", status: "Filed", source: v.source || "Manual",
      added: new Date().toISOString().slice(0, 10), expiry: v.expiry || "—",
      notes: v.notes || "", privacy: v.privacy || "shared",
    }, ...ds]);
    setTimeout(() => scrollTo("documents-detail-view"), 100);
    toast.success(`${v.title.trim()} added.`);
  };

  const memberProfiles = (members && members.length > 0 ? members.slice(0, 6) : []).map((m, idx) => ({
    id: `mp_${m.id || idx}`,
    name: m.name || `Member ${idx + 1}`,
    role: m.role || "Household",
    required: ["Passport","Birth certificate","NHI","School records","Insurance"][idx % 5],
    expiring: "—",
  }));

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="documents" />} phase={phase} testid="documents-page">
      <PageHeading
        testid="documents-hero"
        module="documents"
        art="documents"
        room="Library"
        title="The Library"
        focal={focalSentence(actions, renewals, details)}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setAddOpen(true)} testid="documents-add-document-btn">
              Add document
            </PillButton>
            <TextLink onClick={() => scrollTo("documents-action-queue")} testid="documents-review-actions-btn">Review</TextLink>
            <TextLink onClick={() => scrollTo("documents-capture-inbox")} testid="documents-scan-paper-btn">Scan paper</TextLink>
            <TextLink onClick={() => scrollTo("documents-renewal-watch")} testid="documents-watch-renewals-btn" className="ml-auto">Renewals</TextLink>
          </>
        }
      />

      {/* Hidden preserved-testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>The Library</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Household documents, renewals and captures.</div>
      <div className="hidden" data-testid="documents-summary" aria-hidden>{actions.filter(a=>a.status==='Open').length} to action · {renewals.filter(r=>r.daysLeft<=60).length} expiring soon</div>
      <div className="hidden" data-testid="documents-prepared-by-ara" aria-hidden>Prepared by ARA</div>

      {/* ── Needs attention ──────────────────────────────────────────── */}
      <EditorialSection
        testid="documents-needs-attention"
        moduleMark={{ module: 'documents', label: 'What needs you' }}
        title="Needs attention"
      >
        {(() => {
          const openActions = actions.filter((a) => a.status === "Open").length;
          const soonRenewals = renewals.filter((r) => r.daysLeft <= 60).length;
          if (openActions === 0 && soonRenewals === 0) {
            return (
              <p
                className="italic text-[15px] leading-[1.5]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                data-testid="documents-attention-empty"
              >
                Nothing in the Library needs you right now.
              </p>
            );
          }
          return (
            <p
              className="italic text-[15px] leading-[1.5]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid="documents-attention-summary"
            >
              {openActions > 0 && `${openActions} document${openActions === 1 ? '' : 's'} need${openActions === 1 ? 's' : ''} your attention`}
              {openActions > 0 && soonRenewals > 0 && ' and '}
              {soonRenewals > 0 && `${soonRenewals} renewal${soonRenewals === 1 ? ' is' : 's are'} coming up`}
              .
            </p>
          );
        })()}
      </EditorialSection>

      {/* ── Action queue — the first thing that matters ──────────────── */}
      {actions.length > 0 && (
      <EditorialSection
        testid="documents-action-queue"
        id="documents-action-queue"
        moduleMark={{ module: 'documents', label: 'Attention' }}
        title="Documents needing action"
      >
        <div>
          {actions.map((a, i, arr) => {
            const isDone = a.status === "Done";
            return (
              <div
                key={a.id}
                className={`grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`documents-action-card-${a.id}`}
              >
                <div>
                  <p className={`text-[15.5px] leading-tight tracking-[-0.005em] ${isDone ? 'line-through' : ''}`} style={{ fontFamily: 'Inter, system-ui, sans-serif', color: isDone ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 500 }}>
                    {a.title}
                  </p>
                  <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                    {a.category} · {a.owner} · Due {a.due} · {a.priority}
                  </p>
                </div>
                <div className="flex items-baseline gap-x-5">
                  <TextLink onClick={() => !isDone && markActionDone(a.id)} testid={`action-done-${a.id}`} className={isDone ? 'opacity-40 pointer-events-none' : ''}>
                    Done
                  </TextLink>
                  <TextLink onClick={() => snoozeAction(a.id)} testid={`action-snooze-${a.id}`}>Snooze</TextLink>
                  <TextLink onClick={() => followUpAction(a.id)} testid={`action-followup-${a.id}`}>Follow-up</TextLink>
                </div>
              </div>
            );
          })}
        </div>
      </EditorialSection>
      )}

      {/* ── Renewals ─────────────────────────────────────────────────── */}
      {renewals.length > 0 && (
      <EditorialSection
        testid="documents-renewal-watch"
        id="documents-renewal-watch"
        moduleMark={{ module: 'documents', label: 'Renewals' }}
        title="Coming up"
      >
        <div>
          {renewals.map((r, i, arr) => (
            <div
              key={r.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`documents-renewal-card-${r.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {r.item}
                </p>
                <p className="caption-ara tabular-nums" style={{ color: r.daysLeft <= 30 ? 'var(--ara-ink)' : 'var(--ara-ink-soft)' }}>
                  {r.daysLeft} days
                </p>
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {r.type} · {r.owner} · expires {r.expires}
              </p>
              <div className="mt-2 flex items-baseline gap-x-6">
                <TextLink onClick={() => watchRenewal(r.id)} testid={`renewal-watch-${r.id}`}>Watch</TextLink>
                <TextLink onClick={() => setRenewalReminder(r.id)} testid={`renewal-reminder-${r.id}`}>Reminder</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>
      )}

      {/* ── Capture inbox ────────────────────────────────────────────── */}
      <EditorialSection
        testid="documents-capture-inbox"
        id="documents-capture-inbox"
        moduleMark={{ module: 'documents', label: 'Captures' }}
        title="Waiting for filing"
        aside={approvedCount > 0 ? <p className="caption-ara" data-testid="documents-approved-count">{approvedCount} filed</p> : null}
      >
        {captures.length === 0 ? (
          <p className="prose-ara text-[13.5px] italic" style={{ color: 'var(--ara-ink-soft)' }}>Inbox clear.</p>
        ) : (
          <div>
            {captures.map((c, i, arr) => (
              <div
                key={c.id}
                className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`documents-capture-card-${c.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <Caption>{SOURCE_LABEL[c.source]} · {c.confidence}</Caption>
                  <Caption>{c.owner}</Caption>
                </div>
                <p className="mt-2 text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {c.title}
                </p>
                <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {c.category} · {c.suggested}
                </p>
                <div className="mt-3 flex items-baseline gap-x-6">
                  <TextLink onClick={() => approveCapture(c.id)} testid={`documents-capture-approve-${c.id}`}>Approve →</TextLink>
                  <TextLink onClick={() => editCapture(c.id)} testid={`documents-capture-edit-${c.id}`}>Edit</TextLink>
                  <TextLink onClick={() => dismissCapture(c.id)} testid={`documents-capture-dismiss-${c.id}`}>Dismiss</TextLink>
                  <TextLink onClick={() => junkCapture(c.id)} testid={`documents-capture-junk-${c.id}`}>Junk</TextLink>
                </div>
              </div>
            ))}
          </div>
        )}
      </EditorialSection>

      {/* ── Detail view (with search / filter) ───────────────────────── */}
      <EditorialSection
        testid="documents-detail-view"
        id="documents-detail-view"
        moduleMark={{ module: 'documents', label: 'Library' }}
        title="All documents"
      >
        <div className="mb-5 flex items-center gap-4 flex-wrap">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search…"
            className="flex-1 min-w-[180px] bg-transparent border-b py-2 outline-none text-[14px]"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderColor: 'var(--ara-line)' }}
            data-testid="documents-search-input"
          />
          <div className="flex gap-x-4 flex-wrap">
            {DOCUMENT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(cat)}
                className="caption-ara"
                style={{
                  color: categoryFilter === cat ? 'var(--ara-ink)' : 'var(--ara-ink-soft)',
                  borderBottom: categoryFilter === cat ? '1px solid var(--ara-ink)' : '1px solid transparent',
                  paddingBottom: '2px',
                }}
                data-testid={`documents-filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {filteredDetails.length === 0 ? (
          <p className="prose-ara text-[13.5px] italic" data-testid="documents-empty-state" style={{ color: 'var(--ara-ink-soft)' }}>
            No documents match this search.
          </p>
        ) : (
          <div>
            {filteredDetails.map((d, i, arr) => {
              const isOpen = openDetailId === d.id;
              return (
                <div
                  key={d.id}
                  className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`documents-detail-card-${d.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDetailId(isOpen ? null : d.id)}
                    className="w-full text-left"
                  >
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                        {d.title}
                      </p>
                      <ModuleMark module="documents" label={d.status} />
                    </div>
                    <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                      {d.category} · {d.owner} · added {d.added} · {d.source} · {d.privacy}
                    </p>
                  </button>
                  {isOpen && (
                    <div className="mt-3">
                      <p className="prose-ara text-[13.5px]">{d.notes}</p>
                      <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                        Expiry · {d.expiry}
                      </p>
                      <div className="mt-3 flex items-baseline gap-x-6">
                        <TextLink onClick={() => updateDetailStatus(d.id)} testid={`detail-status-${d.id}`}>Update status</TextLink>
                        <TextLink onClick={() => linkTaskLater(d.id)} testid={`detail-link-${d.id}`}>Link to task</TextLink>
                        <TextLink onClick={() => markPrivate(d.id)} testid={`detail-private-${d.id}`}>Mark private</TextLink>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </EditorialSection>

      {/* ── The Library directory ────────────────────────────────────── */}
      <EditorialSection
        testid="documents-categories"
        moduleMark={{ module: 'documents', label: 'Where everything lives' }}
        title="The Library"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
          {SEED_CATEGORIES.map((cat) => {
            const realCount = details.filter((d) => (d.category || '').toLowerCase() === cat.label.toLowerCase()).length;
            return (
              <div key={cat.id} className="py-3" style={{ borderBottom: '1px solid var(--ara-line-soft)' }} data-testid={`documents-category-card-${cat.id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {cat.label}
                  </p>
                  <span className="caption-ara tabular-nums">{realCount > 0 ? realCount : '—'}</span>
                </div>
                <p className="mt-0.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {cat.sensitive ? 'Private · ' : ''}{cat.examples}
                </p>
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── Family files ─────────────────────────────────────────────── */}
      {memberProfiles.length > 0 && (
      <EditorialSection
        testid="documents-member-profiles"
        moduleMark={{ module: 'documents', label: "What's held for each person" }}
        title="Family files"
      >
        <div>
          {memberProfiles.map((m, i) => {
            const theirDocs = details.filter((d) => (d.owner || '').toLowerCase() === m.name.toLowerCase());
            const theirCats = [...new Set(theirDocs.map((d) => d.category).filter(Boolean))].slice(0, 3);
            return (
              <div
                key={m.id}
                className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === memberProfiles.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`documents-profile-card-${m.id}`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {m.name}
                  </p>
                  <Caption>{theirDocs.length > 0 ? `${theirDocs.length} document${theirDocs.length === 1 ? '' : 's'}` : m.role}</Caption>
                </div>
                <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {theirCats.length > 0 ? theirCats.join(' · ') : `Worth keeping on file · ${m.required}`}
                </p>
              </div>
            );
          })}
        </div>
      </EditorialSection>
      )}

      {/* ── Templates ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="documents-templates"
        moduleMark={{ module: 'documents', label: 'Templates' }}
        title="Reusable letters"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {SEED_TEMPLATES.map((t) => (
            <div key={t.id} data-testid={`documents-template-card-${t.id}`}>
              <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                {t.title}
              </p>
              <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* Preserved testids */}
      <div className="hidden" data-testid="documents-privacy-labels" aria-hidden>{details.filter(d=>d.privacy!=="shared").length} private</div>
      <div className="hidden" data-testid="documents-dashboard" aria-hidden />

      {/* ── ARA's note ───────────────────────────────────────────────── */}
      <EditorialSection
        testid="documents-ara-note"
        moduleMark={{ module: 'documents', label: 'Ara' }}
        title="ARA's note"
      >
        <p
          className="italic text-[15px] leading-[1.55]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch' }}
          data-testid="documents-ara-note-text"
        >
          {details.length === 0
            ? "Your Library is ready when you are. Start with one document you never want to lose."
            : "Everything you've filed is safe here. I'll let you know quietly when something needs renewing."}
        </p>
      </EditorialSection>

      <Colophon>ARA · Library</Colophon>

      <QuickAddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        testid="documents-add-document-modal"
        eyebrow="Library · new document"
        title="File a document"
        submitLabel="File it"
        onSubmit={handleAddDocument}
        fields={[
          { id: 'title',    label: 'Title', type: 'text', required: true, autoFocus: true, placeholder: 'AMI · policy schedule' },
          { id: 'category', label: 'Category', type: 'select', options: DOCUMENT_CATEGORIES.filter(c=>c!=='All'), initial: 'Other' },
          { id: 'owner',    label: 'Owner', type: 'text', placeholder: 'Family' },
          { id: 'source',   label: 'Source', type: 'select', options: ['Paper','Email','PDF','School','Council','Medical','Invoice','Manual'], initial: 'Manual' },
          { id: 'expiry',   label: 'Expiry (optional)', type: 'date' },
          { id: 'privacy',  label: 'Privacy', type: 'select', options: ['shared','private','health','legal','financial'], initial: 'shared' },
          { id: 'notes',    label: 'Notes', type: 'textarea', rows: 2 },
        ]}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
